/* import __COLOCATED_TEMPLATE__ from './conversation-page-command-k-actions.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type Conversation from 'embercom/objects/inbox/conversation';
import type InboxState from 'embercom/services/inbox-state';
import { inject as service } from '@ember/service';
import type Session from 'embercom/services/session';
import { isEmpty } from '@ember/utils';
import { type DurationObject, DurationType } from 'embercom/objects/inbox/duration';
import { isPartTaggedWith } from 'embercom/objects/inbox/taggable-part';
import Tag, { type TagActionType } from 'embercom/objects/inbox/tag';
import type WorkflowConnectorAction from 'embercom/objects/inbox/workflow-connector-action';
import type WorkflowDetails from 'embercom/objects/inbox/workflow-details';
import type SidebarSection from 'embercom/objects/inbox/sidebar-section';
import { type SupportedSidebarSection } from 'embercom/services/inbox-sidebar-service';
import type InboxSidebarService from 'embercom/services/inbox-sidebar-service';
import { taskFor } from 'ember-concurrency-ts';
import type UserComment from 'embercom/objects/inbox/renderable/user-comment';
import { type ConversationResource } from './conversation-resource';
import { TicketCategory, type TicketType } from 'embercom/objects/inbox/ticket';
import type LinkedTicketSummary from 'embercom/objects/inbox/linked-ticket-summary';
import type InboxApi from 'embercom/services/inbox-api';
import type LinkedTicketsResource from './linked-tickets-resource';
import type IntlService from 'embercom/services/intl';
import type PermissionsModal from 'embercom/services/permissions-modal';
import { Channel, CHAT_CHANNELS } from 'embercom/models/data/inbox/channels';
import { type Block } from '@intercom/interblocks.ts';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import type { CustomEmoji } from 'embercom/services/custom-emoji-service';
import { type GifSearchEntry } from 'embercom/objects/inbox/command-k/gifs';
import { PRIORITY_LEVELS } from 'embercom/lib/inbox/priority-levels';
import {
  replyChannelIsMultiParticipant,
  ReplyChannelMap,
} from 'embercom/objects/inbox/composer-pane';
import { ConversationState } from 'embercom/objects/inbox/conversation';
import { type UpdateTicketStateCommandKContext } from 'embercom/objects/inbox/command-k/update-ticket-state';
import { buildUpdateTicketStateCommandKContext } from 'embercom/lib/inbox2/ticket-custom-states';
import type TicketStateService from 'embercom/services/ticket-state-service';
import type TicketCustomState from 'embercom/objects/inbox/ticket-custom-state';
import type ConversationResolveAndCloseService from 'embercom/services/conversation-resolve-and-close-service';
import type MessengerApps from 'embercom/services/messenger-apps';
import type MessengerApp from 'embercom/objects/inbox/messenger-app';
import type InboxConversationStreamBridgeService from 'embercom/services/inbox-conversation-stream-bridge';
import { lookupChannelSupportedType } from 'embercom/helpers/lookup-channel-supported-type';
import { postRequest, ResponseError } from 'embercom/lib/inbox/requests';
import { type TriggerableProcedure } from 'embercom/objects/inbox/command-k/trigger-procedure';
import { captureException } from 'embercom/lib/sentry';

interface Args {
  conversationResource: ConversationResource;
  setTicketTypeToLinkToConversation: (ticketTypeId?: number) => void;
  setTicketTypeToConvertToTicket: (ticketTypeId?: number) => void;
  closeTicketCreationPanel?: () => void;
  closeConversation: () => void;
  linkedTicketsResource: LinkedTicketsResource;
  showReactComposer?: boolean;
}

interface Signature {
  Args: Args;
}

export default class ConversationPageCommandKActionsComponent extends Component<Signature> {
  @service declare inboxState: InboxState;
  @service declare session: Session;
  @service declare inboxSidebarService: InboxSidebarService;
  @service declare intercomEventService: any;
  @service declare inboxApi: InboxApi;
  @service declare intl: IntlService;
  @service declare permissionsModal: PermissionsModal;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare ticketStateService: TicketStateService;
  @service declare conversationResolveAndCloseService: ConversationResolveAndCloseService;
  @service declare messengerApps: MessengerApps;
  @service declare inboxConversationStreamBridge: InboxConversationStreamBridgeService;
  @service declare notificationsService: any;
  @service declare permissionsService: any;

  @tracked tagActionType: TagActionType = null;
  @tracked reactComposerPane: 'reply' | 'note' = 'reply';
  @tracked showUpdateLinkedReportsModal = false;
  @tracked isManagingParticipants = false;
  @tracked selectedTicketState?: ConversationState | TicketCustomState['id'];
  @tracked hasWhatsappTemplateInserted = false;

  private unsubscribeActionsMounted?: () => void;
  private unsubscribeActionsUnmounted?: () => void;
  private unsubscribeWhatsappTemplateChanged?: () => void;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.unsubscribeActionsMounted = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'composer:actions-mounted',
      (data: Record<string, unknown>) => {
        if ((data.conversationId as number) === this.conversation.id) {
          this.reactComposerPane = data.pane === 'note' ? 'note' : 'reply';
        }
      },
    );
    this.unsubscribeActionsUnmounted = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'composer:actions-unmounted',
      () => {
        this.reactComposerPane = 'reply';
      },
    );
    // Track WhatsApp template state from the React composer so that template-only
    // sending (where getCurrentBlocks drops every sibling block) hides the
    // 'Send a ticket form' action in canAddTicket — preventing the silent-drop
    // bug where the agent picks the action, the card visually appears, but only
    // the template ships.
    this.unsubscribeWhatsappTemplateChanged =
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'composer:whatsapp-template-changed',
        (data: Record<string, unknown>) => {
          if ((data.conversationId as number) === this.conversation.id) {
            this.hasWhatsappTemplateInserted = Boolean(data.hasTemplate);
          }
        },
      );
  }

  willDestroy() {
    super.willDestroy();
    this.unsubscribeActionsMounted?.();
    this.unsubscribeActionsUnmounted?.();
    this.unsubscribeWhatsappTemplateChanged?.();
  }

  get conversation(): Conversation {
    return this.args.conversationResource.conversation;
  }

  get lastUserCommentContext() {
    return {
      conversationPart: this.conversation.lastUserComment,
      tags: (this.conversation.lastUserComment?.renderableData as UserComment)?.tags ?? [],
    };
  }

  get firstUserCommentContext() {
    return {
      conversationPart: this.conversation.firstUserComment,
      tags: (this.conversation.firstUserComment?.renderableData as UserComment)?.tags ?? [],
    };
  }

  @action updateTagsForConversationPart(tag: Tag, context: any) {
    this.optimisticUpdateTagsForConversationPart(tag, context);
  }

  @action legacyUpdateTagsForConversationPart(tag: Tag, context: any) {
    let conversationPart = context.conversationPart;
    let partIsTagged = isPartTaggedWith(conversationPart, tag);
    let newTags;
    let { tags: existingTags }: { tags: Array<Tag> } = conversationPart.renderableData;

    if (partIsTagged) {
      newTags = existingTags.filter((t) => t.id !== tag.id);
    } else {
      newTags = [...existingTags, new Tag(tag.id, tag.name, this.session.teammate)];
    }

    this.setTagActionType(partIsTagged ? 'removed' : 'added');
    taskFor(this.inboxState.updateTags).perform(this.conversation, conversationPart, newTags);
  }

  @action optimisticUpdateTagsForConversationPart(tag: Tag, context: any) {
    let conversationPart = context.conversationPart;
    let partIsTagged = isPartTaggedWith(conversationPart, tag);

    this.setTagActionType(partIsTagged ? 'removed' : 'added');

    if (partIsTagged) {
      this.inboxState.removeTag(this.conversation, conversationPart, tag);
    } else {
      this.inboxState.addTag(this.conversation, conversationPart, tag);
    }
  }

  @action triggerWorkflowConnectorAction(action: WorkflowConnectorAction) {
    if (!this.session.teammate.permissions.canUseDataConnectors) {
      this.permissionsModal.showModal({
        permissionsLabel: this.intl.t('inbox.common.permissions-label.can-use-data-connectors'),
      });
      return;
    }

    taskFor(this.inboxState.triggerWorkflowConnectorAction).perform(
      this.conversation,
      null,
      action,
    );
  }

  @action triggerWorkflow(workflowDetails: WorkflowDetails) {
    let sourceId = this.inboxState.commandKSourceConversationId;
    if (sourceId && sourceId !== this.conversation.id) {
      taskFor(this.inboxState.triggerWorkflowById).perform(sourceId, workflowDetails);
    } else {
      taskFor(this.inboxState.triggerWorkflow).perform(this.conversation, workflowDetails);
    }
  }

  get hasDeterministicProcedureTriggers() {
    return this.session.workspace.isFeatureEnabled('team-actions-deterministic-procedure-triggers');
  }

  @action async triggerProcedure(procedure: TriggerableProcedure) {
    try {
      await postRequest(`/ember/inbox/conversations/${this.conversation.id}/trigger_procedure`, {
        procedure_id: procedure.id,
        app_id: this.session.workspace.id,
      });
    } catch (error: unknown) {
      if (await this.showPermissionsModalForError(error)) {
        return;
      }
      let message = await this.parseTriggerProcedureError(error);
      this.notificationsService.notifyError(message);
      let isExpected = error instanceof ResponseError && [403, 422].includes(error.response.status);
      if (!isExpected) {
        captureException(error);
      }
    }
  }

  private async showPermissionsModalForError(error: unknown): Promise<boolean> {
    if (!(error instanceof ResponseError) || error.response.status !== 403) {
      return false;
    }
    try {
      let body = (await error.response.json()) as { required_permission?: string };
      if (body.required_permission) {
        this.permissionsService.loadAllAndShowModal(body.required_permission);
        return true;
      }
    } catch {
      // Fall through to generic error toast
    }
    return false;
  }

  private async parseTriggerProcedureError(error: unknown): Promise<string> {
    let fallback = this.intl.t('inbox.command-k.trigger-procedure.error');
    if (!(error instanceof ResponseError) || error.response.status !== 422) {
      return fallback;
    }
    try {
      let body = (await error.response.json()) as { errors?: string[] };
      return body.errors?.[0] ?? fallback;
    } catch {
      return fallback;
    }
  }

  @action triggerFinAiAgent() {
    taskFor(this.inboxState.triggerFinAiAgent).perform(this.conversation);
  }

  get descriptors() {
    return this.session.workspace.conversationAttributeDescriptors ?? [];
  }

  get hasSharedTaskTickets() {
    if (!this.args.linkedTicketsResource?.tickets) {
      return false;
    }

    return this.args.linkedTicketsResource.tickets
      .filter((ticket: LinkedTicketSummary) => ticket.category === TicketCategory.Task)
      .some((taskTickets: LinkedTicketSummary) => taskTickets.visibleToUser);
  }

  get canAddTicket() {
    let replyChannel = this.conversation.channel.replyChannel;
    let isSupportedChannel =
      CHAT_CHANNELS.includes(replyChannel) ||
      replyChannel === Channel.Slack ||
      replyChannel === Channel.Discord ||
      (replyChannel === Channel.Whatsapp &&
        !this.hasWhatsappTemplateInserted &&
        this.session.workspace.isFeatureEnabled('omni-hosted-ticket-forms-whatsapp')) ||
      (replyChannel === Channel.Instagram &&
        this.session.workspace.isFeatureEnabled('omni-hosted-ticket-forms-instagram'));
    return (
      this.reactComposerPane === 'reply' &&
      isSupportedChannel &&
      !this.conversation.isTicket &&
      Boolean(this.conversation.canShareLinkedTicket)
    );
  }

  @action insertEmoji(emojiValue: string | CustomEmoji) {
    if ((emojiValue as CustomEmoji).isCustomEmoji) {
      let customEmoji = emojiValue as CustomEmoji;
      this.reactTeammateAppIsland.dispatchTeammateAppEvent('composer:emoji-selected', {
        conversationId: this.conversation.id,
        isCustomEmoji: true,
        identifier: customEmoji.identifier,
        src: customEmoji.src,
        alt: customEmoji.alt,
      });
    } else {
      this.reactTeammateAppIsland.dispatchTeammateAppEvent('composer:emoji-selected', {
        conversationId: this.conversation.id,
        isCustomEmoji: false,
        value: emojiValue,
      });
    }
  }

  @action insertGif(gif: GifSearchEntry) {
    this.reactTeammateAppIsland.dispatchTeammateAppEvent('composer:gif-selected', {
      conversationId: this.conversation.id,
      url: gif.url,
      previewUrl: gif.preview_url,
      width: gif.width,
      height: gif.height,
      title: gif.title,
    });
  }

  get displayAppInserters(): boolean {
    return (
      this.reactComposerPane === 'reply' &&
      lookupChannelSupportedType(this.conversation.channel.replyChannel, 'app')
    );
  }

  @action selectMessengerApp(app: MessengerApp) {
    this.inboxConversationStreamBridge.pendingMessengerApp = {
      app,
      conversationId: this.conversation.id,
    };
  }

  get showMergeInto() {
    if (!this.args.showReactComposer) {
      return false;
    }
    let isUnreplyableTicket = this.conversation.isTicket && !this.conversation.isCustomerTicket;
    if (isUnreplyableTicket) {
      return false;
    }
    let hasMultipleParticipants = (this.conversation.participantSummaries?.length ?? 0) > 1;
    let isCrossUserMergeAllowed =
      this.session.workspace.enabledCrossUserConversationMerge &&
      [Channel.Email, Channel.PhoneCall].includes(this.conversation.channel?.initial);
    if (hasMultipleParticipants && !isCrossUserMergeAllowed) {
      return false;
    }
    return true;
  }

  get showManageParticipants() {
    if (!this.args.showReactComposer) {
      return false;
    }
    if (
      this.session.showLightInbox ||
      this.session.shouldShowExternalConversationMode(this.conversation)
    ) {
      return false;
    }
    if (this.conversation.isTicket && !this.conversation.isCustomerTicket) {
      return false;
    }
    let replyChannel = ReplyChannelMap[this.conversation.channel?.replyChannel ?? Channel.Unknown];
    return (
      replyChannelIsMultiParticipant(replyChannel) &&
      this.conversation.channel?.current !== Channel.Slack &&
      this.conversation.channel?.current !== Channel.Discord
    );
  }

  get isManageParticipantsModalOpen() {
    if (!this.args.showReactComposer) {
      return false;
    }
    return (
      this.isManagingParticipants ||
      this.inboxState.showManageParticipantsConversationId === this.conversation.id
    );
  }

  @action showManageParticipantsModal() {
    if (!this.session.teammate.permissions.canManageConversationParticipants) {
      this.permissionsModal.showModal({
        permissionsLabel: this.intl.t(
          'inbox.conversation-header.manage-participants-permission-label',
        ),
      });
      return;
    }
    this.isManagingParticipants = true;
  }

  @action hideManageParticipantsModal() {
    this.isManagingParticipants = false;
    if (this.inboxState.showManageParticipantsConversationId === this.conversation.id) {
      this.inboxState.showManageParticipantsConversationId = null;
    }
  }

  get showUpdateTicketState() {
    if (!this.args.showReactComposer) {
      return false;
    }
    return this.conversation.isTicket && !this.conversation.isClosed;
  }

  get updateTicketStateCommandKContext(): UpdateTicketStateCommandKContext {
    let customStates = this.conversation.ticketType
      ? this.ticketStateService.getLiveCustomStatesForTicketType(this.conversation.ticketType)
      : this.ticketStateService.customStates;
    let currentCustomState = this.ticketStateService.getTicketCustomStateById(
      this.conversation.ticketCustomStateId,
    );
    return buildUpdateTicketStateCommandKContext({
      customStates,
      selectedCustomStateId: currentCustomState?.id,
      allowClosedState: true,
      intl: this.intl,
      conversation: this.conversation,
    });
  }

  get linkedCustomerReportIds() {
    return this.conversation.linkedCustomerReportIds;
  }

  private shouldShowUpdateLinkedReportsModal(
    state: ConversationState | TicketCustomState['id'],
  ): boolean {
    if (!this.conversation.isTrackerTicket || state === ConversationState.Closed) {
      return false;
    }
    return this.linkedCustomerReportIds.length > 0;
  }

  private async changeTicketState(selectedStateId: TicketCustomState['id']) {
    let ticketCustomState = this.ticketStateService.getTicketCustomStateById(selectedStateId);
    await this.inboxState.changeTicketStateLocalUpdates(this.conversation, ticketCustomState);
  }

  @action async handleTicketStateChange(state: ConversationState | TicketCustomState['id']) {
    if (state === ConversationState.Closed) {
      this.conversationResolveAndCloseService.startResolveAndCloseProcess(this.conversation);
      return;
    }
    if (this.shouldShowUpdateLinkedReportsModal(state)) {
      this.selectedTicketState = state;
      this.showUpdateLinkedReportsModal = true;
    } else {
      await this.changeTicketState(state as TicketCustomState['id']);
    }
  }

  @action async onUpdateTrackerWithoutUpdatingLinkedReports() {
    this.showUpdateLinkedReportsModal = false;
    await this.changeTicketState(this.selectedTicketState as TicketCustomState['id']);
  }

  @action async onUpdateTrackerAndLinkedReportStates() {
    this.showUpdateLinkedReportsModal = false;
    await this.changeTicketState(this.selectedTicketState as TicketCustomState['id']);
    await this.inboxApi.bulkChangeTicketState(
      this.linkedCustomerReportIds,
      this.selectedTicketState as TicketCustomState['id'],
    );
  }

  @action insertTicketCardBlock(ticketType: TicketType) {
    let block = {
      icon_url: '',
      state: 'interactive',
      text: this.intl.t('inbox.conversation-reply-composer.ticket-card-block-text'),
      ticket_type_id: ticketType.id,
      ticket_type_title: ticketType.name,
      title: this.intl.t('inbox.conversation-reply-composer.ticket-card-block-title'),
      type: 'createTicketCard',
    } as Block;
    this.reactTeammateAppIsland.dispatchTeammateAppEvent('composer:ticket-card-selected', {
      conversationId: this.conversation.id,
      block,
    });
  }

  get canConvertToTicket() {
    return !this.conversation.isTicket && !this.hasSharedTaskTickets;
  }

  get convertToTicket() {
    return this.args.setTicketTypeToConvertToTicket;
  }

  commandKSnoozeTrackingDataFromSelectValue(duration: DurationObject) {
    return {
      snoozed_until: DurationType[duration.type],
    };
  }

  @action jumpToAppDetails(param: SidebarSection) {
    let sectionId = (
      !isEmpty(param.intercomModuleIdentifier) ? param.intercomModuleIdentifier : param.inboxAppId
    ) as SupportedSidebarSection;
    if (sectionId) {
      let waitBeforeScroll = false;
      if (!this.inboxSidebarService.openSidebarSections.includes(sectionId)) {
        this.inboxSidebarService.onOpenSectionChange(sectionId)(sectionId);
        waitBeforeScroll = true;
      }
      taskFor(this.inboxSidebarService.scrollToSection).perform(sectionId, waitBeforeScroll);
    }
  }

  private checkConversationPermission(): boolean {
    if (!this.session.teammate.permissions.canManageConversationCreation) {
      this.permissionsModal.showModal({
        permissionsLabel: this.intl.t(
          'inbox.conversation-header.create-conversation-permission-label',
        ),
      });
      return false;
    }
    return true;
  }

  @action createSideConversation() {
    if (!this.checkConversationPermission()) {
      return;
    }

    this.inboxSidebarService.openNewSideConversationPanel();
  }

  @action setTicketTypeToLink({ id }: { id: number }) {
    this.args.setTicketTypeToLinkToConversation(id);
  }

  @action setTicketTypeToConvertToTicket({ id }: { id: number }) {
    this.args.setTicketTypeToConvertToTicket(id);
  }

  @action setTagActionType(type: TagActionType) {
    this.tagActionType = type;
  }

  @action togglePriority() {
    this.inboxState.changePriority(
      this.conversation,
      this.conversation.priority ? PRIORITY_LEVELS.NONE : PRIORITY_LEVELS.PRIORITY,
    );
  }

  // Multi-level handler invoked from the Command+K picker. The pane emits the
  // chosen level value verbatim; we delegate to inbox-state's level-aware
  // path so the optimistic part carries the correct previousPriority.
  @action setPriorityLevel(priority: number) {
    this.inboxState.setPriorityLevel(this.conversation, priority);
  }

  @action onSnoozeSelect(duration: DurationObject) {
    this.args.conversationResource.snoozeConversation(duration);

    this.args.closeTicketCreationPanel && this.args.closeTicketCreationPanel();
  }

  @action openCopilot() {
    this.inboxSidebarService.openCopilot();
  }

  get togglePriorityTrackingData() {
    return {
      action: 'changed',
      object: 'priority',
      section: 'conversation_details',
      marked_as_priority: this.conversation.priority,
      inbox_type: this.inboxState.activeInbox?.type,
      layout_type: this.inboxState.activeConversationsView,
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationPageCommandKActions': typeof ConversationPageCommandKActionsComponent;
    'inbox2/conversation-page-command-k-actions': typeof ConversationPageCommandKActionsComponent;
  }
}
